import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    INNER_RADIUS,
    useDensity,
    formatRelativeTime,
} from './SharedFrames';

const VyasaModelScores = ({ scores, asOf }) => {
    const { compact } = useDensity();

    return (
        <Box sx={{ mt: compact ? 1 : 1.25 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.6 }}>
                <Typography
                    variant="caption"
                    sx={(theme) => ({
                        color: theme.palette.text.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        fontWeight: 600,
                    })}
                >
                    Model Scores
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {asOf ? `updated ${formatRelativeTime(asOf)}` : 'no data yet'}
                </Typography>
            </Stack>
            <Box
                sx={(theme) => ({
                    borderRadius: INNER_RADIUS,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.82),
                    backgroundColor: theme.palette.mode === 'dark'
                        ? alpha('#0f172a', 0.58)
                        : alpha('#ffffff', 0.88),
                    overflow: 'hidden',
                })}
            >
                <Box
                    sx={(theme) => ({
                        display: 'grid',
                        gridTemplateColumns: '1.6fr 0.9fr 0.8fr 0.9fr 0.9fr',
                        gap: 0,
                        px: compact ? 1 : 1.2,
                        py: compact ? 0.5 : 0.65,
                        backgroundColor: alpha(theme.palette.divider, 0.18),
                        fontSize: '0.72rem',
                        color: theme.palette.text.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                    })}
                >
                    <span>Combo</span>
                    <span style={{ textAlign: 'right' }}>Current</span>
                    <span style={{ textAlign: 'right' }}>Thresh</span>
                    <span style={{ textAlign: 'right' }}>Expected</span>
                    <span style={{ textAlign: 'right' }}>Strike</span>
                </Box>
                {(scores && scores.length > 0) ? scores.map((s) => {
                    const current = Number(s.current ?? 0);
                    const threshold = Number(s.threshold ?? 0);
                    const expected = Number(s.expected ?? 0);
                    const gap = threshold - current;
                    const nearFiring = gap >= 0 && gap <= 0.10;
                    const fired = current >= threshold;
                    const tone = fired ? 'success' : nearFiring ? 'warning' : 'default';
                    return (
                        <Box
                            key={s.label}
                            sx={(theme) => ({
                                display: 'grid',
                                gridTemplateColumns: '1.6fr 0.9fr 0.8fr 0.9fr 0.9fr',
                                gap: 0,
                                px: compact ? 1 : 1.2,
                                py: compact ? 0.55 : 0.7,
                                borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                alignItems: 'center',
                                fontVariantNumeric: 'tabular-nums',
                                fontSize: compact ? '0.82rem' : '0.88rem',
                            })}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: 'inherit',
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {nearFiring && !fired ? '► ' : ''}{s.label}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={(theme) => ({
                                    fontSize: 'inherit',
                                    textAlign: 'right',
                                    fontWeight: 700,
                                    color:
                                        tone === 'success' ? theme.palette.success.main
                                            : tone === 'warning' ? theme.palette.warning.main
                                                : theme.palette.text.primary,
                                })}
                            >
                                {Number.isFinite(current) ? current.toFixed(3) : '–'}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: 'inherit', textAlign: 'right', color: 'text.secondary' }}>
                                {Number.isFinite(threshold) ? threshold.toFixed(2) : '–'}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: 'inherit', textAlign: 'right', color: 'text.secondary' }}>
                                {Number.isFinite(expected) && expected > 0 ? expected.toFixed(3) : '–'}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: 'inherit', textAlign: 'right', color: 'text.secondary' }}>
                                {Number.isFinite(Number(s.strike)) ? `K${Math.round(Number(s.strike))}` : '–'}
                            </Typography>
                        </Box>
                    );
                }) : (
                    <Box sx={{ px: compact ? 1 : 1.2, py: compact ? 0.9 : 1.1 }}>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            Waiting for first decision cycle…
                        </Typography>
                    </Box>
                )}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
                Current = top score across all strikes this cycle · Expected = walk-forward mean AUC from training · ► = within 0.10 of threshold
            </Typography>
        </Box>
    );
};

export default VyasaModelScores;
